import type {
  EmbryoMonogenicFinding,
  EmbryoPolygenicRisk,
} from "@src/packages/cycles/hooks/useEmbryoReport";

/** Artwork identifiers and exact label aliases, not a catalogue of patient results. */
export const CONDITION_ILLUSTRATIONS = [
  { key: "breast-cancer", aliases: ["Breast cancer", "Cancer du sein"] },
  {
    key: "male-breast-cancer",
    aliases: [
      "Male breast cancer",
      "Breast cancer in males",
      "Cancer du sein masculin",
    ],
  },
  {
    key: "type-2-diabetes",
    aliases: [
      "Type 2 diabetes",
      "Type II diabetes",
      "Type 2 diabetes mellitus",
      "Diabète de type 2",
    ],
  },
  {
    key: "type-1-diabetes",
    aliases: [
      "Type 1 diabetes",
      "Type I diabetes",
      "Type 1 diabetes mellitus",
      "Diabète de type 1",
    ],
  },
  {
    key: "coronary-artery-disease",
    aliases: [
      "Coronary artery disease",
      "Coronary heart disease",
      "CAD",
      "Maladie coronarienne",
    ],
  },
  {
    key: "hypertension",
    aliases: ["Hypertension", "High blood pressure", "Hypertension artérielle"],
  },
  {
    key: "atrial-fibrillation",
    aliases: [
      "Atrial fibrillation",
      "Fibrillation atriale",
      "Fibrillation auriculaire",
    ],
  },
  {
    key: "alzheimer-disease",
    aliases: [
      "Alzheimer's disease",
      "Alzheimer disease",
      "Alzheimer's",
      "Maladie d'Alzheimer",
    ],
  },
  { key: "schizophrenia", aliases: ["Schizophrenia", "Schizophrénie"] },
  {
    key: "bipolar-disorder",
    aliases: ["Bipolar disorder", "Bipolar", "Trouble bipolaire"],
  },
  {
    key: "depression",
    aliases: [
      "Depression",
      "Major depression",
      "Major depressive disorder",
      "Dépression",
      "Trouble dépressif majeur",
    ],
  },
  {
    key: "anxiety-disorders",
    aliases: [
      "Anxiety disorders",
      "Anxiety disorder",
      "Anxiety",
      "Troubles anxieux",
    ],
  },
  {
    key: "obsessive-compulsive-disorder",
    aliases: [
      "Obsessive-compulsive disorder",
      "OCD",
      "Trouble obsessionnel-compulsif",
      "TOC",
    ],
  },
  {
    key: "adhd",
    aliases: [
      "ADHD",
      "Attention deficit hyperactivity disorder",
      "Attention-deficit/hyperactivity disorder",
      "TDAH",
    ],
  },
  {
    key: "autism-spectrum-disorder",
    aliases: [
      "Autism spectrum disorder",
      "Autism spectrum disorders",
      "Autistic spectrum disorders",
      "Autism",
      "Trouble du spectre de l'autisme",
    ],
  },
  { key: "insomnia", aliases: ["Insomnia", "Insomnie"] },
  { key: "migraine", aliases: ["Migraine", "Migraines"] },
  {
    key: "parkinson-disease",
    aliases: [
      "Parkinson's disease",
      "Parkinson disease",
      "Maladie de Parkinson",
    ],
  },
  {
    key: "multiple-sclerosis",
    aliases: ["Multiple sclerosis", "Sclérose en plaques"],
  },
  {
    key: "restless-legs-syndrome",
    aliases: [
      "Restless legs syndrome",
      "Restless leg syndrome",
      "Syndrome des jambes sans repos",
    ],
  },
  {
    key: "alcohol-dependence",
    aliases: [
      "Alcohol dependence",
      "Alcohol use disorder",
      "Dépendance à l'alcool",
    ],
  },
  {
    key: "asthma",
    aliases: ["Asthma", "Childhood asthma", "Asthme", "Asthme de l'enfant"],
  },
  {
    key: "seasonal-allergies",
    aliases: [
      "Seasonal allergies",
      "Allergic rhinitis",
      "Hay fever",
      "Allergies saisonnières",
    ],
  },
  {
    key: "celiac-disease",
    aliases: [
      "Celiac disease",
      "Coeliac disease",
      "Maladie cœliaque",
      "Maladie coeliaque",
    ],
  },
  {
    key: "inflammatory-bowel-disease",
    aliases: [
      "Inflammatory bowel disease",
      "IBD",
      "Maladies inflammatoires chroniques de l'intestin",
      "MICI",
    ],
  },
  {
    key: "class-3-obesity",
    aliases: [
      "Class III obesity",
      "Class 3 obesity",
      "Obesity",
      "Obésité de classe III",
      "Obésité",
    ],
  },
  { key: "endometriosis", aliases: ["Endometriosis", "Endométriose"] },
  {
    key: "polycystic-ovary-syndrome",
    aliases: [
      "Polycystic ovary syndrome",
      "Polycystic ovarian syndrome",
      "PCOS",
      "Syndrome des ovaires polykystiques",
      "SOPK",
    ],
  },
  { key: "ovarian-cancer", aliases: ["Ovarian cancer", "Cancer de l'ovaire"] },
  {
    key: "prostate-cancer",
    aliases: ["Prostate cancer", "Cancer de la prostate"],
  },
  {
    key: "colorectal-cancer",
    aliases: ["Colorectal cancer", "Colon cancer", "Cancer colorectal"],
  },
  {
    key: "age-related-macular-degeneration",
    aliases: [
      "Age-related macular degeneration",
      "Macular degeneration",
      "AMD",
      "Dégénérescence maculaire liée à l'âge",
      "DMLA",
    ],
  },
  {
    key: "rheumatoid-arthritis",
    aliases: ["Rheumatoid arthritis", "Polyarthrite rhumatoïde"],
  },
  { key: "osteoarthritis", aliases: ["Osteoarthritis", "Arthrose"] },
  { key: "psoriasis", aliases: ["Psoriasis"] },
  {
    key: "severe-acne",
    aliases: ["Severe acne", "Acne", "Acné sévère", "Acné"],
  },
  {
    key: "male-pattern-baldness",
    aliases: [
      "Male-pattern baldness",
      "Male pattern baldness",
      "Androgenetic alopecia",
      "Alopécie androgénétique",
    ],
  },
  { key: "respiratory-system", aliases: ["Cystic fibrosis", "Mucoviscidose"] },
  {
    key: "neuromuscular-system",
    aliases: ["Spinal muscular atrophy", "SMA", "Amyotrophie spinale"],
  },
  {
    key: "genetic-system",
    aliases: [
      "Multisystem genetic disorder",
      "Mitochondrial disease",
      "Birth defects",
      "Vision and hearing loss",
    ],
  },
  {
    key: "neurologic-system",
    aliases: [
      "Neurodevelopmental delay",
      "Intellectual disability",
      "Fragile X syndrome",
      "Tay-Sachs disease",
      "Epilepsy",
      "Dementia",
    ],
  },
  {
    key: "cardiovascular-system",
    aliases: [
      "Heart defects",
      "Congenital heart disease",
      "Long QT syndrome",
      "Brugada syndrome",
      "Hypertrophic cardiomyopathy",
    ],
  },
  {
    key: "digestive-system",
    aliases: ["Digestive disorders", "Congenital intestinal disorder"],
  },
  {
    key: "renal-system",
    aliases: [
      "Kidney disease",
      "Polycystic kidney disease",
      "Wilms' tumor",
      "Wilms tumor",
    ],
  },
  {
    key: "hepatic-system",
    aliases: ["Liver disease", "Inherited liver disease"],
  },
  {
    key: "hematologic-system",
    aliases: [
      "Sickle cell",
      "Sickle cell disease",
      "Sickle cell anemia",
      "Hemophilia A",
      "Hemophilia B",
      "Alpha thalassemia",
      "Beta thalassemia",
    ],
  },
  {
    key: "immune-system",
    aliases: [
      "Severe combined immunodeficiency",
      "SCID",
      "Inherited immunodeficiency",
    ],
  },
  {
    key: "cutaneous-system",
    aliases: ["Ichthyosis", "Epidermolysis bullosa", "Inherited skin disorder"],
  },
  {
    key: "visual-system",
    aliases: [
      "Vision loss",
      "Retinoblastoma",
      "Retinitis pigmentosa",
      "Glaucoma",
    ],
  },
  {
    key: "auditory-system",
    aliases: [
      "Hearing loss",
      "Nonsyndromic hearing loss",
      "Congenital hearing loss",
    ],
  },
  {
    key: "musculoskeletal-system",
    aliases: [
      "Osteoporosis",
      "Osteogenesis imperfecta",
      "Inherited skeletal disorder",
    ],
  },
  {
    key: "endocrine-system",
    aliases: [
      "Congenital adrenal hyperplasia",
      "Congenital hypothyroidism",
      "Inherited endocrine disorder",
    ],
  },
  {
    key: "metabolic-system",
    aliases: [
      "Phenylketonuria",
      "PKU",
      "Pompe disease",
      "Gaucher disease",
      "Inherited metabolic disorder",
    ],
  },
  {
    key: "connective-tissue-system",
    aliases: [
      "Marfan syndrome",
      "Ehlers-Danlos syndrome",
      "Inherited connective tissue disorder",
    ],
  },
  {
    key: "hereditary-cancer-system",
    aliases: [
      "Hereditary cancer",
      "Hereditary cancers",
      "Li-Fraumeni syndrome",
      "Lynch syndrome",
      "Melanoma",
      "Thyroid cancer",
      "Bladder cancer",
      "Pancreatic cancer",
    ],
  },
] as const;

export type ConditionIllustrationKey =
  (typeof CONDITION_ILLUSTRATIONS)[number]["key"];
type ReportedCondition =
  EmbryoMonogenicFinding["condition"] | EmbryoPolygenicRisk["condition"];

function normalizeConditionLabel(label: string): string {
  return label
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const illustrationsByLabel = new Map<string, ConditionIllustrationKey>(
  CONDITION_ILLUSTRATIONS.flatMap(({ key, aliases }) =>
    aliases.map((label) => [normalizeConditionLabel(label), key] as const),
  ),
);

/** Unknown lab labels stay unknown; never infer an organ from a gene or substring. */
export function getConditionIllustrationKey(
  condition: ReportedCondition,
): ConditionIllustrationKey | undefined {
  return illustrationsByLabel.get(normalizeConditionLabel(condition));
}
